/**
 * Date and time utility functions
 */

import * as Api from '@ViewModels';
import moment from 'moment';
import { formatBirthdayDisplay } from '../BirthdayUtils';

/**
 * Gets default date string value in MM/DD/YYYY format
 * @source Original location: UiUtils.ts.backup line 983
 */
export const getDefaultDateStringValue = (date: moment.MomentInput) => {
	return moment(date).format('MM/DD/YYYY');
};

/**
 * Gets default time string value in h:mma format
 * @source Original location: UiUtils.ts.backup line 987
 */
export const getDefaultTimeStringValue = (date: moment.MomentInput) => {
	return moment(date).format('h:mma');
};

// Re-export formatBirthdayDisplay from BirthdayUtils (complex version with keyFact and contact)
export { formatBirthdayDisplay };

/**
 * Simple birthday display formatter that returns just the text
 * @param birthday - Birthday key fact
 * @param contact - Contact information
 * @returns Formatted birthday text string
 */
export const formatBirthdayText = (birthday: Api.IKeyFact, contact: Api.IProjectedContact): string => {
	const result = formatBirthdayDisplay(birthday, contact);
	return result.text;
};

/**
 * Formats a birthday string to MM/DD/YYYY format with timezone handling
 * For birthdays, we extract the date part only to avoid timezone conversion issues
 * @param birthday - The birthday string to format
 * @returns Formatted birthday in MM/DD/YYYY format or original string if parsing fails
 * @source Added from master: New simple string-based birthday formatter
 */
export const formatBirthdayString = (birthday: string): string => {
	if (!birthday) return '';

	// For birthdays, we need to extract just the date part to avoid timezone issues
	// Handle ISO date strings like "2001-12-12T00:00:00Z" or "2001-12-12"
	let dateString = birthday;

	// If it's an ISO string with time, extract just the date part
	if (birthday.includes('T')) {
		dateString = birthday.split('T')[0];
	}

	// Parse the date string and format it
	const date = moment(dateString, 'YYYY-MM-DD', true);
	if (date.isValid()) {
		return date.format('MM/DD/YYYY');
	}

	// If that fails, try the standard validation and formatting for other date formats
	const standardResult = validateAndFormatDate(birthday);
	if (standardResult.isValid) {
		return standardResult.formattedDate;
	}

	// Return original string if all parsing attempts failed
	return birthday;
};

/**
 * Validates and formats a date string
 * @source Original location: UiUtils.ts.backup line 991
 */
export const validateAndFormatDate = (dateString: string): { isValid: boolean; formattedDate: string } => {
	if (!dateString) return { isValid: false, formattedDate: '' };

	// Let moment parse the date with its built-in intelligent parsing
	const date = moment(dateString.trim());

	if (date.isValid()) {
		return { isValid: true, formattedDate: date.format('MM/DD/YYYY') };
	}

	return { isValid: false, formattedDate: dateString };
};

/**
 * Checks if a date is today
 * @source Original location: UiUtils.ts.backup line 1116
 */
export const dateIsToday = (date?: moment.MomentInput) =>
	date ? moment().utc(false).startOf('day').isSameOrBefore(date, 'day') : false;

/**
 * Gets friendly time interval since now
 * @source Original location: UiUtils.ts.backup line 1195
 */
export const getFriendlyTimeIntervalSinceNow = (date: moment.MomentInput, includeTimeOfDay = false) => {
	if (!date) {
		return null;
	}

	const now = moment();
	const then = moment(date);
	const minutes = now.diff(then, 'minutes');
	if (minutes < 0) {
		return null;
	}

	if (minutes < 5) {
		return 'Just now';
	}

	if (minutes < 60) {
		return `${minutes} minutes ago`;
	}

	const timeOfDay = `${includeTimeOfDay ? ` at ${then.format('LT')}` : ''}`;

	const hours = now.diff(then, 'hours');
	if (hours < 24) {
		return `${hours} hour${hours === 1 ? '' : 's'} ago${timeOfDay}`;
	}

	const days = now.diff(then, 'days');
	if (days < 7) {
		return `${days} day${days === 1 ? '' : 's'} ago${timeOfDay}`;
	}

	const weeks = now.diff(then, 'weeks');
	if (weeks < 4) {
		return `${weeks} week${weeks === 1 ? '' : 's'} ago${timeOfDay}`;
	}

	const months = now.diff(then, 'months');
	if (months < 12) {
		return `${months} month${months === 1 ? '' : 's'} ago${timeOfDay}`;
	}

	const years = now.diff(then, 'years');
	return `${years} year${years === 1 ? '' : 's'} ago${timeOfDay}`;
};

/**
 * Gets campaign calendar date range string value
 * @source Original location: UiUtils.ts.backup line 1566
 */
export const getCampaignCalendarDateRangeStringValue = (start: moment.MomentInput, end?: moment.MomentInput) => {
	let completedDayStringValue = '';
	if (!end) {
		return `${moment(start).format('MMMM Do')}`;
	}

	const startMoment = moment(start);
	const endMoment = moment(end);
	const startDay = startMoment.get('day');
	const endDay = endMoment.get('day');
	const startMonth = startMoment.get('month');
	const endMonth = endMoment.get('month');
	if (startDay !== endDay || startMonth !== endMonth) {
		completedDayStringValue = moment(end).format(`${startMonth !== endMonth ? 'MMMM ' : ''}Do`);
	}

	return `${moment(start).format('MMMM Do')}${completedDayStringValue ? ` — ${completedDayStringValue}` : ''}`;
};

/**
 * Gets campaign calendar date range value
 * @source Original location: UiUtils.ts.backup line 1585
 */
export const getCampaignCalendarDateRangeValue = (start: moment.MomentInput, end?: moment.MomentInput) => {
	let completedDayStringValue = '';
	if (!end) {
		return `${moment(start).format('DD')}`;
	}
	const startMoment = moment(start);
	const endMoment = moment(end);
	const startDay = startMoment.get('day');
	const endDay = endMoment.get('day');
	const startMonth = startMoment.get('month');
	const endMonth = endMoment.get('month');

	if (startDay !== endDay || startMonth !== endMonth) {
		completedDayStringValue = moment(end).format(`${startMonth !== endMonth ? 'MMMM ' : ''}Do`);
	}

	return `${moment(start).format('DD')}${completedDayStringValue ? ` \u2014 ${completedDayStringValue}` : ''}`;
};

/**
 * Formats date criteria for filtering
 * @source Original location: UiUtils.ts.backup line 2525
 */
export function formatDateCriteria<T>(property: T, value?: Api.IRange<Date>) {
	if (!value) {
		return null;
	}

	const f = 'YYYY-MM-DD';
	const criteria = {
		criteria: [
			{
				op: Api.FilterOperator.Gt,
				property,
				value: moment(value?.start).format(f),
			},
			...(value?.end
				? [
						{
							op: Api.FilterOperator.Lt,
							property,
							value: moment(value.end).add(1, 'day').format(f),
						},
					]
				: []),
		],
	};

	return criteria;
}

/**
 * Converts an ISO 8601 datetime string to YYYY-MM-DD format for the DateInput component
 * @param isoString - ISO 8601 datetime string (e.g., "2025-06-01T00:00:00Z")
 * @returns YYYY-MM-DD formatted string or empty string if invalid/null
 */
export const isoToDateInputFormat = (isoString?: string | null): string => {
	if (!isoString) return '';
	const date = moment(isoString);
	return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

/**
 * Converts YYYY-MM-DD format to ISO 8601 datetime string for API calls
 * @param dateString - YYYY-MM-DD formatted string
 * @returns ISO 8601 datetime string (e.g., "2025-06-01T00:00:00.000Z") or null if invalid/empty
 */
export const dateInputToIsoFormat = (dateString?: string): string | null => {
	if (!dateString?.trim()) return null;
	const date = moment(dateString, 'YYYY-MM-DD', true);
	return date.isValid() ? date.toISOString() : null;
};
